import type { PaymentAccountDetails } from "../context/paymentsTypes";

export const PAYMENTS_SETUP_FORM_DRAFT_KEY = "glossa_payments_setup_form_v1";

export type PaymentSetupFormDraft = {
  fullName: string;
  email: string;
  businessName: string;
};

export function getPaymentSetupFormDraft(): PaymentSetupFormDraft | null {
  try {
    const raw = localStorage.getItem(PAYMENTS_SETUP_FORM_DRAFT_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as Partial<PaymentSetupFormDraft>;
    if (
      typeof p.fullName !== "string" ||
      typeof p.email !== "string" ||
      typeof p.businessName !== "string"
    ) {
      return null;
    }
    return { fullName: p.fullName, email: p.email, businessName: p.businessName };
  } catch {
    return null;
  }
}

export function setPaymentSetupFormDraft(draft: PaymentSetupFormDraft): void {
  localStorage.setItem(PAYMENTS_SETUP_FORM_DRAFT_KEY, JSON.stringify(draft));
}

function readUserFromLocalStorage(): Record<string, unknown> | null {
  try {
    const raw = localStorage.getItem("user");
    if (!raw) return null;
    const u = JSON.parse(raw) as unknown;
    return u && typeof u === "object" ? (u as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

/** Defaults when no draft: `user.company.company_name`, then localStorage user, then saved payment account. */
export function getPaymentSetupInitialValues(
  authUser: unknown,
  storedAccount: PaymentAccountDetails | null
): PaymentSetupFormDraft {
  const saved = getPaymentSetupFormDraft();
  if (saved) return saved;

  const u =
    (authUser && typeof authUser === "object" ? (authUser as Record<string, unknown>) : null) ??
    readUserFromLocalStorage();

  const company =
    u?.company && typeof u.company === "object"
      ? (u.company as Record<string, unknown>)
      : null;

  const name =
    (typeof u?.name === "string" && u.name) ||
    (typeof u?.full_name === "string" && u.full_name) ||
    "";

  const email = typeof u?.email === "string" ? u.email : "";

  /** Prefer API shape `company.company_name`; fall back to `company.name`. */
  const businessFromCompany = (() => {
    if (!company) return "";
    const cn = company.company_name;
    if (typeof cn === "string" && cn.trim()) return cn.trim();
    const legacy = company.name;
    if (typeof legacy === "string" && legacy.trim()) return legacy.trim();
    return "";
  })();

  const business = businessFromCompany || storedAccount?.companyName?.trim() || "";

  return {
    fullName: name.trim(),
    email: email.trim(),
    businessName: business.trim(),
  };
}

export function buildPaymentAccountFromStripeSetup(
  form: PaymentSetupFormDraft
): PaymentAccountDetails {
  return {
    companyName: form.businessName.trim(),
    contactFullName: form.fullName.trim(),
    contactEmail: form.email.trim(),
    addressLine1: "",
    addressLine2: "",
    city: "",
    region: "",
    postalCode: "",
    country: "",
    logoDataUrl: null,
    transactionFeePerTxn: 0,
    vatNumber: "",
    additionalNotes: "",
  };
}
